export type MediaRatio = 'portrait' | 'landscape' | 'square' | 'detail';

export interface ProjectImage {
  src: string;
  alt: string;
  ratio: MediaRatio;
  focal: string;
  /** Included in the curated Hero marquee pool. */
  hero?: boolean;
}

export interface ProjectCredits {
  client: string;
  creativeDirector: string;
  photography: string;
  styling: string;
  talent: string;
  retouch: string;
}

export interface Project {
  slug: string;
  order: number;
  title: string;
  category: string;
  year: number;
  location: string;
  cover: ProjectImage;
  images: ProjectImage[];
  statement: string;
  credits: ProjectCredits;
}

export const studio = {
  name: 'Maren Voss',
  descriptor: 'Studio',
  email: 'studio@marenvoss.com',
  instagram: '@marenvoss.studio',
  instagramUrl: 'https://instagram.com',
  location: 'Berlin / Lisbon',
};

export const projects: Project[] = [
  {
    slug: 'citrus-club',
    order: 1,
    title: 'Chromatic Form',
    category: 'Fashion / Campaign',
    year: 2026,
    location: 'Amalfi Coast, Italy',
    cover: {
      src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_01-chromatic-form-01-campaign-hero.webp',
      alt: 'Chromatic Form fashion campaign hero image',
      ratio: 'landscape',
      focal: 'center center',
    },
    images: [
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_01-chromatic-form-01-campaign-hero.webp',
        alt: 'Chromatic Form fashion campaign hero image',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_01-chromatic-form-02-two-person-frame.webp',
        alt: 'Chromatic Form two-person campaign frame',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_01-chromatic-form-03-beauty-closeup.webp',
        alt: 'Chromatic Form beauty close-up',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_01-chromatic-form-04-material-detail.webp',
        alt: 'Chromatic Form material detail',
        ratio: 'landscape',
        focal: 'center center',
      },
    ],
    statement: 'A summer capsule shot in three sunny days on the Amalfi coast. Citrus, sea air and a cast of friends — the brief was simple: make people want to be there.',
    credits: {
      client: 'Arket',
      creativeDirector: 'Jonas Lindqvist',
      photography: 'Maren Voss',
      styling: 'Mira Okafor',
      talent: 'Lena Holm, Theo Marchetti / IMG',
      retouch: 'Studio Halden',
    },
  },
  {
    slug: 'golden-hour-crew',
    order: 2,
    title: 'Halse / Skin Studies',
    category: 'Beauty / Stills',
    year: 2026,
    location: 'Lisbon, Portugal',
    cover: {
      src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_02-halse-skin-studies-01-beauty-hero.webp',
      alt: 'Halse Skin Studies beauty hero portrait',
      ratio: 'landscape',
      focal: 'center center',
    },
    images: [
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_02-halse-skin-studies-01-beauty-hero.webp',
        alt: 'Halse Skin Studies beauty hero portrait',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_02-halse-skin-studies-02-product-still.webp',
        alt: 'Halse skincare product still life',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_02-halse-skin-studies-03-human-product.webp',
        alt: 'Model holding a Halse skincare product',
        ratio: 'landscape',
        focal: 'center center',
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_02-halse-skin-studies-04-macro-material.webp',
        alt: 'Macro texture of Halse skincare formula',
        ratio: 'landscape',
        focal: 'center center',
      },
    ],
    statement: 'An editorial about friendship and the last hour of light. No poses, just a playlist, a rooftop and people who actually like each other.',
    credits: {
      client: 'Kinfolk Magazine',
      creativeDirector: 'Elise Marchetti',
      photography: 'Maren Voss',
      styling: 'Joana Ribeiro',
      talent: 'Awa Sissoko, Rui Costa / Elite',
      retouch: 'Studio Halden',
    },
  },
  {
    slug: 'sunday-faces',
    order: 3,
    title: 'Objects After Dark',
    category: 'Still Life / Luxury',
    year: 2025,
    location: 'Paris, France',
    cover: {
      src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_03-objects-after-dark-01-fragrance-hero.webp',
      alt: 'Objects After Dark fragrance bottle still life',
      ratio: 'landscape',
      focal: 'center center',
    },
    images: [
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_03-objects-after-dark-01-fragrance-hero.webp',
        alt: 'Objects After Dark fragrance bottle still life',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_03-objects-after-dark-02-jewelry-metal.webp',
        alt: 'Jewelry and polished metal still life',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_03-objects-after-dark-03-abstract-detail.webp',
        alt: 'Abstract luxury still life detail',
        ratio: 'landscape',
        focal: 'center center',
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_03-objects-after-dark-04-accessory-frame.webp',
        alt: 'Luxury accessory still life frame',
        ratio: 'landscape',
        focal: 'center center',
      },
    ],
    statement: 'Twelve portraits made on slow Sunday mornings in Paris. Every sitter got coffee, good light and permission to laugh.',
    credits: {
      client: 'Self-initiated',
      creativeDirector: 'Maren Voss',
      photography: 'Maren Voss',
      styling: 'Camille Durand',
      talent: 'Various sitters',
      retouch: 'Maren Voss',
    },
  },
  {
    slug: 'color-block-city',
    order: 4,
    title: 'Sculpted Motion',
    category: 'Fashion / Editorial',
    year: 2025,
    location: 'Mexico City, Mexico',
    cover: {
      src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_04-sculpted-motion-01-fashion-hero.webp',
      alt: 'Sculpted Motion fashion editorial hero image',
      ratio: 'landscape',
      focal: 'center center',
    },
    images: [
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_04-sculpted-motion-01-fashion-hero.webp',
        alt: 'Sculpted Motion fashion editorial hero image',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_04-sculpted-motion-02-mirror-frame.webp',
        alt: 'Fashion model framed in a mirror',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_04-sculpted-motion-03-body-garment.webp',
        alt: 'Sculptural garment on the body',
        ratio: 'landscape',
        focal: 'center center',
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_04-sculpted-motion-04-motion-frame.webp',
        alt: 'Fashion garment captured in motion',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
    ],
    statement: 'A fashion story built around painted walls and hard midday sun. Every look was matched to a street, so the city becomes the palette.',
    credits: {
      client: 'Numéro',
      creativeDirector: 'Lucía Herrera',
      photography: 'Maren Voss',
      styling: 'Diego Salas',
      talent: 'Valeria Ruiz / Bravo',
      retouch: 'Post Office',
    },
  },
  {
    slug: 'pool-days',
    order: 5,
    title: 'City Heat',
    category: 'Fashion / Location',
    year: 2025,
    location: 'Palm Springs, USA',
    cover: {
      src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_05-city-heat-01-location-hero.webp',
      alt: 'City Heat fashion location hero image',
      ratio: 'landscape',
      focal: 'center center',
    },
    images: [
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_05-city-heat-01-location-hero.webp',
        alt: 'City Heat fashion location hero image',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_05-city-heat-02-male-portrait.webp',
        alt: 'Male fashion portrait on location',
        ratio: 'landscape',
        focal: 'center center',
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_05-city-heat-03-two-person-frame.webp',
        alt: 'Two models in a city street fashion frame',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_05-city-heat-04-street-detail.webp',
        alt: 'Street fashion detail',
        ratio: 'landscape',
        focal: 'center center',
      },
    ],
    statement: 'A resort swim campaign made for social first. Big color, real splashes and a cast who were happy to get wet.',
    credits: {
      client: 'Solid & Striped',
      creativeDirector: 'Oliver Grant',
      photography: 'Maren Voss',
      styling: 'Fiona McLeod',
      talent: 'Saoirse Kelly, Ewan Price / Storm',
      retouch: 'Studio Halden',
    },
  },
  {
    slug: 'fresh-skin',
    order: 6,
    title: 'Faces, Quietly',
    category: 'Portrait',
    year: 2024,
    location: 'Milan, Italy',
    cover: {
      src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_06-faces-quietly-01-mature-woman.webp',
      alt: 'Quiet portrait of a mature woman',
      ratio: 'landscape',
      focal: 'center center',
    },
    images: [
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_06-faces-quietly-01-mature-woman.webp',
        alt: 'Quiet portrait of a mature woman',
        ratio: 'landscape',
        focal: 'center center',
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_06-faces-quietly-02-environmental-man.webp',
        alt: 'Environmental portrait of a man',
        ratio: 'landscape',
        focal: 'center center',
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_06-faces-quietly-03-close-portrait.webp',
        alt: 'Close portrait in soft light',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_06-faces-quietly-04-older-man.webp',
        alt: 'Portrait of an older man',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
    ],
    statement: 'A beauty editorial about real skin and one bold color at a time. Minimal makeup, maximum light.',
    credits: {
      client: 'Vogue Italia',
      creativeDirector: 'Giulia Ferri',
      photography: 'Maren Voss',
      styling: 'Marco Bellini',
      talent: 'Adut Deng, Sofia Rossi / Women',
      retouch: 'Post Milano',
    },
  },
  {
    slug: 'soft-hours',
    order: 7,
    title: 'Soft Hours',
    category: 'Lifestyle / Hospitality',
    year: 2024,
    location: 'Mediterranean Coast',
    cover: {
      src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_07-soft-hours-01-hero-portrait.webp',
      alt: 'Relaxed portrait of a woman in a sunlit Mediterranean hotel setting',
      ratio: 'landscape',
      focal: 'center center',
    },
    images: [
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_07-soft-hours-01-hero-portrait.webp',
        alt: 'Relaxed portrait of a woman in a sunlit Mediterranean hotel setting',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_07-soft-hours-02-lifestyle-interaction.webp',
        alt: 'Two guests sharing breakfast and coffee in natural daylight',
        ratio: 'landscape',
        focal: 'center center',
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_07-soft-hours-03-environment-wide.webp',
        alt: 'Wide view of a poolside Mediterranean hotel terrace',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_07-soft-hours-04-atmosphere-detail.webp',
        alt: 'Poolside still life with citrus, glassware and linen in sunlight',
        ratio: 'landscape',
        focal: 'center center',
      },
    ],
    statement: 'A hospitality story about the slow hours between breakfast and the pool. Linen, citrus, warm light and guests who have nowhere else to be.',
    credits: {
      client: 'Cover Magazine',
      creativeDirector: 'Freja Holm',
      photography: 'Maren Voss',
      styling: 'Ida Sørensen',
      talent: 'Nora Lind, Karim Idrissi / Scoop',
      retouch: 'Studio Halden',
    },
  },
  {
    slug: 'open-studio',
    order: 8,
    title: 'Table for Six',
    category: 'Hospitality / Food / Lifestyle',
    year: 2024,
    location: 'Berlin, Germany',
    cover: {
      src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_08-table-for-six-01-group-hero.webp',
      alt: 'Group of six sharing a meal around a table',
      ratio: 'landscape',
      focal: 'center center',
    },
    images: [
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_08-table-for-six-01-group-hero.webp',
        alt: 'Group of six sharing a meal around a table',
        ratio: 'landscape',
        focal: 'center center',
        hero: true,
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_08-table-for-six-02-group-alt.webp',
        alt: 'Alternate frame of the group dinner',
        ratio: 'landscape',
        focal: 'center center',
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_08-table-for-six-03-individual-interaction.webp',
        alt: 'Guest interacting at the dinner table',
        ratio: 'portrait',
        focal: 'center 30%',
      },
      {
        src: 'https://public.readdy.ai/ai/img_res/sandbox/ac93a43e8bd7181410fc5ae371caca21/366e41cf-ce30-42ea-b24d-019cd850fca0_08-table-for-six-04-food-human-detail.webp',
        alt: 'Hands and food detail at the table',
        ratio: 'portrait',
        focal: 'center center',
        hero: true,
      },
    ],
    statement: 'Brand portraits for a Berlin design collective. Bright backdrops, real workspaces and faces people instantly trust.',
    credits: {
      client: 'Studio Verhaegen',
      creativeDirector: 'Maren Voss',
      photography: 'Maren Voss',
      styling: 'In-house',
      talent: 'Studio team',
      retouch: 'Maren Voss',
    },
  },
];